// create a context for the post

import {
	createContext,
	type Dispatch,
	type SetStateAction,
	useContext,
	useState,
} from 'react'
import { type ISupportedLanguage } from '#app/utils/i18n.js'
import { type ACTION_INTENTS } from '../types'

type PostMeta = {
	title: string
	slug: string
	category: string
	coverImage: string | File
	publishedAt: string | null
}

type Actions = (typeof ACTION_INTENTS)[keyof typeof ACTION_INTENTS]

type AllImages = { id: string; postId: string }[]

export type PostContextType = {
	intent: Actions
	postId: string | null
	currentLanguage: ISupportedLanguage

	allImages?: AllImages
	categories: {
		label: string
		value: string
	}[]

	meta: PostMeta
	setMeta: Dispatch<SetStateAction<PostMeta>>
}

export const PostContext = createContext<PostContextType | null>(null)

export function PostProvider({
	currentLanguage,
	children,
	intent,
	data,
	allImages,
	categories,
}: {
	intent: Actions
	currentLanguage: ISupportedLanguage
	children: React.ReactNode
	data?: Partial<PostMeta> & {
		postId?: string | null
	}
	allImages?: AllImages
	categories: {
		label: string
		value: string
	}[]
}) {
	const [meta, setMeta] = useState<PostMeta>({
		title: data?.title ?? '',
		slug: data?.slug ?? '',
		category: data?.category ?? '',
		coverImage: data?.coverImage ?? '',
		publishedAt: data?.publishedAt ?? null,
	})

	return (
		<PostContext.Provider
			value={{
				intent,
				postId: data?.postId ?? null,
				currentLanguage,
				meta,
				setMeta,
				allImages,
				categories,
			}}
		>
			{children}
		</PostContext.Provider>
	)
}

export function usePost() {
	const context = useContext(PostContext)
	if (!context) {
		throw new Error('usePost must be used within a PostProvider')
	}
	return context
}
